import { faWifi, faFileExport, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import { RowRecord, TableColumn } from 'react-data-table-component';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Button, Message, Col, Panel, FlexboxGrid, Alert } from 'rsuite';
import { saveAs } from 'file-saver';
import LPSensorHistoric from '../../handlers/history/LPSensorHistoric.handler';
import { authHeader } from '../../redux/helpers';
import { axiosService } from '../../redux/services';
import PanelHeader from '../Custom/PanelHeader';
import HistoryTable from '../ReactDataTableComponent/HistoryTable';
import HistorySortPart from './SortPart';
import LPSensorExpandable from './Table/Expandable/LPSensorExpandable';
import LPMatrixDisplayV2Expandable from './Table/Expandable/LPMatrixDisplayV2Expandable';
import LPSensorDescCell from './Table/LPSensorDescCell';
import LPSensorOptionCell from './Table/LPSensorOptionCell';

type Props = WrappedComponentProps;

type State = {
    histories: Array<Record<string, any>>;
    isLoading: boolean;
    hasError: boolean;
    error: string;
    users: Array<Record<string, any>>;
    formValues: FormValues;
};

type FormValues = {
    user: string | null;
    before: Date | null;
    after: Date | null;
    nbElements: number;
};

class LPSensorHistory extends React.Component<Props, State> {
    columns: TableColumn<RowRecord>[];

    constructor(props: Props) {
        super(props);

        this.state = {
            histories: [],
            isLoading: true,
            hasError: false,
            error: '',
            users: [],
            formValues: {
                user: null,
                before: null,
                after: null,
                nbElements: 100,
            },
        };
        this.exportLPSensorHistory = this.exportLPSensorHistory.bind(this);

        this.columns = [
            {
                name: this.props.intl.formatMessage({
                    id: 'history.lpSensor.user',
                }),
                selector: 'user',
                sortable: true,
                cell: row => {
                    if (row.user === 'MQTT') {
                        return <FontAwesomeIcon icon={faRobot} size="lg" />;
                    } else {
                        return row.user;
                    }
                },
            },
            {
                name: this.props.intl.formatMessage({
                    id: 'history.lpSensor.createdAt',
                }),
                selector: 'createdAt',
                sortable: true,
            },
            {
                name: this.props.intl.formatMessage({
                    id: 'history.lpSensor.lpSensorName',
                }),
                selector: 'lpSensorName',
                sortable: true,
            },
            {
                name: this.props.intl.formatMessage({
                    id: 'history.lpSensor.desc',
                }),
                selector: 'desc',
                sortable: true,
                cell: row => LPSensorDescCell(row.desc),
            },
            {
                name: this.props.intl.formatMessage({
                    id: 'controller.history.option',
                }),
                selector: 'option',
                cell: row => LPSensorOptionCell(row.desc, row.option),
            },
        ];
    }

    componentDidMount() {
        this.loadHistory();
    }

    loadHistory = () => {
        this.setState({
            isLoading: true,
            error: '',
            hasError: false,
        });

        axiosService
            .getAxios()
            .post(
                '/historics/lp-sensors',
                {
                    nbResult: this.state.formValues.nbElements || 100,
                    user: this.state.formValues.user,
                    after: this.state.formValues.after ? moment(this.state.formValues.after).toISOString() : null,
                    before: this.state.formValues.before ? moment(this.state.formValues.before).toISOString() : null,
                },
                { headers: authHeader() }
            )
            .then(lpSensorHistoryResponse => {
                let users: Array<string> = [];

                const histories = lpSensorHistoryResponse.data.map(history => {
                    const lpSensorHistory = new LPSensorHistoric(history);

                    users.indexOf(lpSensorHistory.getUser()) === -1 && users.push(lpSensorHistory.getUser());

                    return lpSensorHistory.getHistory();
                });
                this.setState({
                    histories,
                    users: users.map(user => {
                        return { label: user, value: user };
                    }),
                });
            })
            .catch(err => {
                this.setState({
                    hasError: true,
                    error: err,
                });
            })
            .finally(() => {
                this.setState({
                    isLoading: false,
                });
            });
    };

    setUser = user => {
        this.setState({
            formValues: { ...this.state.formValues, user },
        });
    };

    setBefore = before => {
        this.setState({
            formValues: { ...this.state.formValues, before },
        });
    };

    setAfter = after => {
        this.setState({
            formValues: { ...this.state.formValues, after },
        });
    };

    setNbElements = nbElements => {
        this.setState({
            formValues: { ...this.state.formValues, nbElements },
        });
    };

    exportLPSensorHistory() {
        let allText = '';
        let nbLines = this.state.histories.length;
        let sortLines = this.state.formValues.nbElements;
        let sortUser = this.state.formValues.user;
        let sortBegining = moment(this.state.formValues.before).toISOString();
        let sortStop = moment(this.state.formValues.after).toISOString();
        let fileName = 'export-LPSensorHistory';

        if (nbLines > 0) {
            if ('-' + sortUser != '-null') {
                allText = allText + 'user:' + sortUser;
                fileName = fileName + '-user:' + sortUser;
            }
            if (sortStop + '-' + sortBegining != 'null-null') {
                allText = allText + ';start: ' + sortBegining + ';end: ' + sortStop;
                fileName = fileName + '-start:' + sortBegining + '-end:' + sortStop;
            } else {
                allText = allText + ';;';
            }

            fileName = fileName + '-lines:' + sortLines + '-date:' + moment().format('YYYY-MM-DD_HH:mm:ss') + '.csv';
            allText =
                allText +
                ';lines: ' +
                sortLines +
                '\nhistory.LPSensor;nbLines: ' +
                nbLines +
                '\nuser;createdAt;lpSensorName;description;option\n';

            for (let i = 0; i < nbLines; i++) {
                allText =
                    allText +
                    this.state.histories[i].user +
                    ';' +
                    this.state.histories[i].createdAt +
                    ';' +
                    this.state.histories[i].lpSensorName +
                    ';' +
                    this.state.histories[i].desc +
                    ';' +
                    JSON.stringify(this.state.histories[i].option) +
                    '\n';
            }

            const csvData = new Blob([allText], { type: 'text/csv;charset=utf-8;' });
            saveAs(csvData, fileName);

            Alert.success(this.props.intl.formatMessage({ id: 'LPSensorHistory.exportCSV.success' }));
        } else {
            Alert.error(this.props.intl.formatMessage({ id: 'LPSensorHistory.exportCSV.error' }));
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <Message type="error">
                    <FormattedMessage id="history.lpSensor.fetchHistoryError" values={{ error: this.state.error }} />
                </Message>
            );
        } else {
            return (
                <Panel
                    className="panel-big dashboard-card-header"
                    shaded
                    bordered
                    header={
                        <PanelHeader
                            icon={faWifi}
                            iconRotation={180}
                            title={this.props.intl.formatMessage({ id: 'history.lpSensor' })}
                            buttons={[
                                <FlexboxGrid.Item componentClass={Col} xs={2.5}>
                                    <Button
                                        color="orange"
                                        block
                                        onClick={this.exportLPSensorHistory}
                                        data-cy="history-export">
                                        <FontAwesomeIcon icon={faFileExport} />
                                        Exporter
                                    </Button>
                                </FlexboxGrid.Item>,
                            ]}
                        />
                    }
                    bodyFill>
                    <div style={{ marginTop: 10, marginBottom: 10 }}>
                        <HistorySortPart
                            users={this.state.users}
                            setUser={this.setUser}
                            setBefore={this.setBefore}
                            setAfter={this.setAfter}
                            valid={this.loadHistory}
                            setNbElements={this.setNbElements}
                        />
                    </div>
                    <HistoryTable
                        columns={this.columns}
                        data={this.state.histories}
                        progressPending={this.state.isLoading}
                        expandableRows
                        expandOnRowClicked
                        expandableRowDisabled={row => row.disabled}
                        expandableRowsComponent={LPSensorExpandable}
                    />
                </Panel>
            );
        }
    }
}

export default injectIntl(LPSensorHistory);
